import type { Role } from "../components/auth/AuthContext";

/**
 * 설정(/settings) 좌측 메뉴의 코드 카탈로그 — CRM·IT 사이드바의 menuCodeDefaults 에 해당한다.
 *
 * 메뉴 권한 관리 > 설정 메뉴 탭은 CRM·IT 와 **같은 편집기**(MenuStructureEditor, area "settings")로
 * 이 카탈로그 + DB 오버라이드(menu_items / menu_role_map)를 편집한다 — 이름·경로·순서·소속·
 * 상위/하위 메뉴 추가·등급(게스트~)·노출. 설정 사이드바(SettingsLayout)도 같은 트리로 그린다.
 *
 * 잠금(자물쇠 방지 · "계정 설정은 누구나"):
 *  - 계정 섹션 · 계정 설정: 등급·노출 고정(로그인한 전원).
 *  - 메뉴 권한 관리: 노출 고정(숨기면 되돌릴 화면이 없다). 조상 섹션이 꺼져도 보인다.
 *    super_admin 은 등급 판정을 항상 통과하므로 등급으로는 스스로를 잠글 수 없다.
 *
 * 화면 등급 ≠ 서버 데이터 등급. apiRole 보다 낮게 열면 화면만 열리고 목록이 비거나 오류가 난다.
 */
export type SettingsMenuDef = {
  key: string;            // menu_role_map / menu_items 의 menu_key
  label: string;
  icon: string;           // menuIcons 이름
  path?: string;          // 섹션은 없음
  parent?: string;        // 항목의 코드 기본 섹션
  defaultRole: Role;
  /** 이 화면의 서버 조회 API 가 요구하는 최소 등급 */
  apiRole?: Role;
  /** 등급 고정 */
  roleLocked?: boolean;
  /** 노출 고정(끌 수 없음, 조상이 꺼져도 노출) */
  alwaysVisible?: boolean;
  /** 사이드바 활성 표시에 같이 쓰는 경로 */
  activePaths?: string[];
};

export const SETTINGS_KEY_PREFIX = "settings:";
export const SETTINGS_ROOT_KEY = "topnav:SETTINGS";

/** 코드 순서 = 배열 순서. 섹션 → 그 섹션 항목 순. */
export const SETTINGS_MENUS: SettingsMenuDef[] = [
  { key: "settings:account", label: "계정", icon: "UserRound", defaultRole: "guest", roleLocked: true, alwaysVisible: true },
  { key: "settings:profile", label: "계정 설정", icon: "UserRound", path: "/settings/profile", parent: "settings:account",
    defaultRole: "guest", roleLocked: true, alwaysVisible: true, activePaths: ["/settings/profile", "/settings/integrations"] },
  { key: "settings:admin", label: "관리자", icon: "ShieldCheck", defaultRole: "guest" },
  { key: "settings:users", label: "유저 관리", icon: "ShieldCheck", path: "/settings/users", parent: "settings:admin", defaultRole: "super_admin", apiRole: "super_admin" },
  { key: "settings:menus", label: "메뉴 권한 관리", icon: "ListChecks", path: "/settings/menus", parent: "settings:admin", defaultRole: "admin", alwaysVisible: true },
  { key: "settings:documents", label: "문서 관리", icon: "FileText", path: "/settings/documents", parent: "settings:admin", defaultRole: "admin" },
  { key: "settings:organization", label: "조직 관리", icon: "Building2", path: "/settings/organization", parent: "settings:admin", defaultRole: "super_admin" },
  // 화면이 고르는 목록(Application · Opportunity 상태 · 업종)의 정본 — crm_masters. 쓰기 API 는 admin+(sales-handler masters)
  { key: "settings:master-data", label: "마스터 데이터", icon: "Database", path: "/settings/master-data", parent: "settings:admin", defaultRole: "admin", apiRole: "admin" },
  { key: "settings:announcements", label: "공지 관리", icon: "Megaphone", path: "/settings/announcements", parent: "settings:admin", defaultRole: "admin", apiRole: "admin" },
  // 산출물 연결 1차 — 포트 등록부 보기 · 시험 실행(읽기 전용). 서버 API 는 로그인만 요구(viewer 도 읽는다) — 화면은 admin+
  { key: "settings:data-flow", label: "데이터 흐름", icon: "GitBranch", path: "/settings/data-flow", parent: "settings:admin", defaultRole: "admin" },
];

export const SETTINGS_ROLE_OPTIONS: Role[] = ["guest", "viewer", "admin", "super_admin"];
export const SETTINGS_CODE_ORDER: string[] = SETTINGS_MENUS.map((m) => m.key);

const BY_KEY = new Map(SETTINGS_MENUS.map((m) => [m.key, m]));

export const isSettingsMenuKey = (key: string): boolean => key.startsWith(SETTINGS_KEY_PREFIX);
export const settingsMenuDef = (key: string): SettingsMenuDef | undefined => BY_KEY.get(key);
export const isSettingsSection = (key: string): boolean => {
  const def = BY_KEY.get(key);
  return !!def && !def.parent;
};

/** 코드 기본 부모 — 항목은 섹션, 섹션은 없음(null). */
export function settingsCodeParent(key: string): string | null {
  return BY_KEY.get(key)?.parent ?? null;
}

/** 설정 메뉴의 등급 — 고정이면 기본값, 아니면 DB 값 > 기본값. */
export function resolveSettingsRole(key: string, configured: Role | undefined): Role {
  const def = BY_KEY.get(key);
  if (def?.roleLocked) return def.defaultRole;
  return configured ?? def?.defaultRole ?? "admin";
}

/** 설정 메뉴의 노출 — 고정이면 항상 노출. */
export function resolveSettingsEnabled(key: string, configured: boolean | undefined): boolean {
  if (BY_KEY.get(key)?.alwaysVisible) return true;
  return configured !== false;
}

/** 조상이 꺼져도 보이는 메뉴인가 (메뉴 권한 관리 · 계정 설정) */
export const isAlwaysVisibleSettingsMenu = (key: string): boolean => !!BY_KEY.get(key)?.alwaysVisible;
